import React, { useEffect, useState } from 'react';
import { PublicHeader } from '../components/layout/PublicHeader';
import { PublicFooter } from '../components/layout/PublicFooter';
import { getJobOpenings, JobOpening } from '../services/api';

export const Careers: React.FC = () => {
    const [jobs, setJobs] = useState<JobOpening[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchJobs = async () => {
            try {
                const data = await getJobOpenings();
                setJobs(data);
            } catch (error) {
                console.error("Failed to fetch job openings:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchJobs();
    }, []);

    // Helper to get color based on job type/department (simplified logic)
    const getBadgeColor = (type: string) => {
        if (type === 'REMOTE') return 'blue';
        if (type === 'HYBRID') return 'purple';
        return 'green';
    };

    return (
        <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col transition-colors duration-200 font-body">
            <PublicHeader />

            <div className="flex-1 w-full max-w-[1400px] mx-auto px-4 sm:px-10 pt-28 pb-12">
                <div className="bg-gradient-to-br from-[#161b22] to-[#0d1117] rounded-xl p-12 border border-blue-500/20 shadow-lg text-center mb-16 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-green-500"></div>
                    <h1 className="text-4xl sm:text-6xl font-bold font-display text-white mb-6">Únete a la Misión</h1>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
                        Estamos buscando mentes brillantes y curiosas para construir el futuro de la seguridad digital. ¿Listo para el desafío?
                    </p>
                    <button className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-all shadow-lg shadow-blue-500/20">
                        Ver Posiciones Abiertas
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
                    <div className="lg:col-span-2 space-y-6">
                        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                            <span className="material-symbols-outlined text-green-400">work</span>
                            Oportunidades Disponibles
                        </h2>

                        {loading ? (
                            <div className="flex justify-center p-8">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                            </div>
                        ) : (
                            <>
                                {jobs.map((job) => {
                                    const color = getBadgeColor(job.type);
                                    return (
                                        <div key={job.id} className={`bg-white dark:bg-[#1a2332] p-6 rounded-xl border border-gray-200 dark:border-[#282e39] hover:border-${color}-500/50 transition-colors group cursor-pointer`}>
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <h3 className={`font-bold text-lg text-slate-900 dark:text-white group-hover:text-${color}-400 transition-colors`}>{job.title}</h3>
                                                    <p className="text-sm text-gray-500">{job.type_display} · {job.department}</p>
                                                </div>
                                                <span className={`px-3 py-1 bg-${color}-500/10 text-${color}-400 rounded-full text-xs font-bold`}>{job.department}</span>
                                            </div>
                                            <p className="text-slate-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                                                {job.description}
                                            </p>
                                            <div className="flex gap-2 text-xs text-gray-500">
                                                <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[14px]">schedule</span> Publicado: {new Date(job.posted_at).toLocaleDateString()}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                                {jobs.length === 0 && (
                                    <div className="bg-[#161b22] p-8 rounded-xl border border-white/5 text-center">
                                        <p className="text-gray-400">No hay posiciones abiertas en este momento.</p>
                                    </div>
                                )}
                            </>
                        )}

                    </div>

                    <div>
                        <div className="bg-[#161b22] p-6 rounded-xl border border-white/5 sticky top-28">
                            <h3 className="font-bold text-white mb-4">¿Por qué FortifyTech?</h3>
                            <ul className="space-y-4">
                                <li className="flex gap-3">
                                    <span className="material-symbols-outlined text-yellow-400">emoji_events</span>
                                    <span className="text-sm text-gray-400">Cultura de aprendizaje continuo y certificaciones pagadas.</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="material-symbols-outlined text-blue-400">health_and_safety</span>
                                    <span className="text-sm text-gray-400">Seguro médico privado y beneficios de bienestar.</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="material-symbols-outlined text-purple-400">laptop_mac</span>
                                    <span className="text-sm text-gray-400">Equipamiento de última generación (MacBook Pro / Dell XPS).</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="material-symbols-outlined text-green-400">holiday_village</span>
                                    <span className="text-sm text-gray-400">Horarios flexibles y política de vacaciones ilimitadas.</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

            </div>

            <PublicFooter />
        </div>
    );
};
